import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { theme } from '@hhgtech/hhg-components'
import {
  Select,
  Button,
  Text,
  Modal,
  DatePicker,
  useMantineLocale,
} from '@hhgtech/hhg-components/mantine'
import { Box, Flex } from '@mantine/core'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { ArrowButton } from 'components/pages/healthTools/due-date/component/arrowButton'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath, assetSvgPath } from 'interfaces/constants'
import { DATE_FORMAT_BY_LOCALE } from 'interfaces/constants/pregenancyCategory'
import { WeekOfPregnancyApi } from 'interfaces/types'
import { fastdom } from 'utils/fastdom'

import { CardContent } from './cardContent'
import {
  EmotionSelectStyles,
  modalStyles,
  StyledDueDateCalculatorCardBody,
  StyledSelectedWeek,
  StyledToolRecommendation,
} from './index.styled'
import { WeekOption } from './selectItem'

export type DueDateCalculatorCardProps = {
  onClickGoToOvulationTool: (babyBirthday?: Date) => void
  dueDateWeeks: Array<WeekOfPregnancyApi>
  currentWeek: number
  firstDay: Date
  selectedWeek: number
  onClickWeek?(): void
}

type CardHeaderProps = {
  selectedWeek: number
  firstDay: Date
  dueDateWeeks: Array<WeekOfPregnancyApi>
  onSelectWeek?(value: string): void
  onClickWeek?(): void
}
const CardHeader = ({
  selectedWeek,
  firstDay,
  dueDateWeeks,
  onSelectWeek,
  onClickWeek,
}: CardHeaderProps) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()
  const { locale } = useRouterExtend()
  const { isLoaded } = useMantineLocale(locale)
  const firstDateOfWeek = dayjs(firstDay).add(
    Number(selectedWeek - 1) * 7,
    'day',
  )
  const lastDateOfWeek = dayjs(firstDay).add(
    Number(selectedWeek - 1) * 7 + 6,
    'day',
  )

  const pregnancyWeekSelectOptions = useMemo(() => {
    return Array.from({ length: 44 }).map((_, index) => {
      return {
        value: String(index + 1),
        fruitIcon: dueDateWeeks[index]?.fruit_icon,
        firstDay,
        label: f(
          {
            id: 'dueDateTool.weekNumber',
            defaultMessage: 'Week {weekNumber}',
          },
          {
            weekNumber: index + 1,
          },
        ),
      }
    })
  }, [firstDay, dueDateWeeks])
  useEffect(() => {
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        const _input = document.querySelector<HTMLInputElement>(
          '#select-header.mantine-Input-input',
        )
        if (_input) {
          fastdom.mutate(() => {
            _input.classList.add('pointer-event-child-none')
            _input.dataset.eventCategory = 'Health Tool'
            _input.dataset.eventAction = 'Due Date Week Click'
            _input.dataset.eventLabel = currentUrl
            _input.style.fontSize = 'inherit'
            _input.style.lineHeight = 'inherit'
            _input.style.height = 'inherit'
          })
        }
      }
    }, 1000)
  }, [])
  if (!isLoaded) return null
  return isMobile ? (
    <Box
      onClick={onClickWeek}
      className="pointer-event-child-none"
      data-event-category="Health Tool"
      data-event-action="Due Date Week Click"
      data-event-label={currentUrl}
    >
      <StyledSelectedWeek
        size="h4"
        weight="semiBold"
        color={theme.colors.gray800}
      >
        <span>
          {f(
            {
              id: 'dueDateTool.weekNumber',
              defaultMessage: 'Week {weekNumber}',
            },
            {
              weekNumber: selectedWeek,
            },
          )}
        </span>
        <img
          src={`${assetSvgPath}/arrow-down.svg`}
          alt=""
          loading="lazy"
          width={8}
        />
      </StyledSelectedWeek>
      <Text size="p3" color={theme.colors.gray600}>
        {`${firstDateOfWeek.date()} - ${lastDateOfWeek.format(
          DATE_FORMAT_BY_LOCALE[locale],
        )} `}
      </Text>
    </Box>
  ) : (
    <>
      <Select
        id="select-header"
        size="md"
        maxDropdownHeight={300}
        data={pregnancyWeekSelectOptions}
        value={String(selectedWeek)}
        itemComponent={WeekOption}
        variant="unstyled"
        inputContainer={(children) => (
          <Box pos="relative">
            {children}
            <StyledSelectedWeek
              size="h4"
              weight="semiBold"
              data-absolute="true"
              color={theme.colors.gray800}
            >
              <span>
                {f(
                  {
                    id: 'dueDateTool.weekNumber',
                    defaultMessage: 'Week {weekNumber}',
                  },
                  {
                    weekNumber: selectedWeek,
                  },
                )}
              </span>
              <img
                src={`${assetSvgPath}/arrow-down.svg`}
                alt=""
                loading="lazy"
              />
            </StyledSelectedWeek>
          </Box>
        )}
        styles={EmotionSelectStyles}
        onChange={onSelectWeek}
        rightSection={<></>}
      />
      <Text size="p4" color={theme.colors.gray600}>
        {`${firstDateOfWeek.format(
          DATE_FORMAT_BY_LOCALE[locale],
        )} - ${lastDateOfWeek.format(DATE_FORMAT_BY_LOCALE[locale])} `}
      </Text>
    </>
  )
}

export function DueDateCalculatorCard({
  firstDay,
  onClickGoToOvulationTool,
  dueDateWeeks,
  currentWeek,
  selectedWeek: _selectedWeek,
  onClickWeek,
}: DueDateCalculatorCardProps) {
  const { formatMessage: f } = useIntl()
  const [
    isToolRecommendationOpen,
    setIsToolRecommendationOpen,
  ] = useState<boolean>(false)
  const isMobile = useIsMobile()
  const [babyBirthday, setBabyBirthday] = useState<Date | null>(null)
  const [babyBirthdayError, setBabyBirthdayError] = useState(false)
  const [selectedWeek, setSelectedWeek] = useState(0)
  useEffect(() => {
    setSelectedWeek(isMobile ? _selectedWeek : currentWeek)
  }, [_selectedWeek, currentWeek])

  const onCloseModal = useCallback(() => {
    setIsToolRecommendationOpen(false)
  }, [])

  const onChangeBabyBirthday = useCallback((value: Date) => {
    setBabyBirthday(value)
    setBabyBirthdayError(false)
  }, [])

  const onClickTrackPeriod = useCallback(() => {
    if (!babyBirthday) {
      setBabyBirthdayError(true)
    } else {
      onClickGoToOvulationTool(babyBirthday)
    }
  }, [babyBirthday, onClickGoToOvulationTool])

  return (
    <>
      <StyledDueDateCalculatorCardBody>
        <div className="card-header">
          <ArrowButton
            position="left"
            className="left-arrow"
            style={{ display: selectedWeek < 2 ? 'none' : 'flex' }}
            onClick={() => {
              setSelectedWeek((pre) => pre - 1)
            }}
          />
          <CardHeader
            onSelectWeek={(value: string) => setSelectedWeek(Number(value))}
            firstDay={firstDay}
            dueDateWeeks={dueDateWeeks}
            selectedWeek={selectedWeek}
            onClickWeek={onClickWeek}
          />

          <ArrowButton
            position="right"
            className="right-arrow"
            onClick={() => setSelectedWeek((pre) => pre + 1)}
            style={{ display: selectedWeek > 43 ? 'none' : 'flex' }}
          />
        </div>
        <CardContent
          selectedWeek={selectedWeek}
          currentWeek={currentWeek}
          dueDateWeeks={dueDateWeeks}
          onReachClick={() => setIsToolRecommendationOpen(true)}
          onTrackPeriod={onClickGoToOvulationTool}
        />
      </StyledDueDateCalculatorCardBody>
      <Modal
        styles={modalStyles}
        onClose={onCloseModal}
        opened={isToolRecommendationOpen}
      >
        <StyledToolRecommendation>
          <Flex pos="absolute" top={16} right={16} onClick={onCloseModal}>
            <img
              loading="lazy"
              src="/images/close-icon.svg"
              alt=""
              width={13}
              height={13}
            />
          </Flex>
          <img
            loading="lazy"
            src={`${assetImagePath}/due-date-reached.svg`}
            alt="baby-vaccine"
            className="due-date-reached-image"
          />
          <Text size="h3" as="h2" color={theme.colors.gray800}>
            {f({ id: 'health.youAreParent' })}
          </Text>
          <Text size="p2" color={theme.colors.gray600}>
            {f({
              id: 'dueDatePage.reach_update_baby_birthday',
              defaultMessage:
                'Look like you have reached your due date, please update your baby birthday.',
            })}
          </Text>
          <Box w="100%" mb={isMobile ? 16 : 24}>
            <DatePicker
              value={babyBirthday}
              onChange={onChangeBabyBirthday}
              size="md"
              placeholder="DD/MM/YYYY"
              error={babyBirthdayError}
              // dropdownType="modal"
              popoverProps={{ withinPortal: true }}
            />
          </Box>
          <Flex gap={8} direction="column" w="100%">
            <Button
              variant="primary"
              size="md"
              onClick={onClickTrackPeriod}
              fullWidth
            >
              {f({ id: 'pregnancyTool.dueDateCard.trackPeriod' })}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={onCloseModal}
              fullWidth
            >
              {f({ id: 'common.skip' })}
            </Button>
          </Flex>
        </StyledToolRecommendation>
      </Modal>
    </>
  )
}
